import React from "react";
import { renderTextWithPlaceholders as rT } from "./PlaceholderToken";

export default function ResumePreviewModern({ data, editable = false, onPlaceholderFill }) {
  const c = data?.contact || {};
  const onFill = editable ? onPlaceholderFill : undefined;

  const contactFields = [
    { val: c.email,    path: "contact.email"    },
    { val: c.phone,    path: "contact.phone"    },
    { val: c.location, path: "contact.location" },
    { val: c.linkedin, path: "contact.linkedin" },
  ].filter(x => x.val);

  return (
    <div className="bg-white font-sans text-[#1a1a1a] text-[13px] leading-relaxed max-w-[750px] mx-auto shadow-sm flex">
      {/* Left sidebar */}
      <div className="w-[220px] flex-shrink-0 bg-[#0F172A] text-white px-5 py-7 min-h-full">
        {c.name && (
          <div className="mb-5">
            <h1 className="text-[18px] font-bold leading-tight mb-2">
              {rT(c.name, "contact.name", editable, onFill)}
            </h1>
            <div className="w-8 h-0.5 bg-[#38BDF8]" />
          </div>
        )}
        {contactFields.length > 0 && (
          <div className="mb-6">
            <SideLabel>Contact</SideLabel>
            {contactFields.map((x) => (
              <div key={x.path} className="text-[10px] text-slate-300 mb-0.5 break-all">
                {rT(x.val, x.path, editable, onFill)}
              </div>
            ))}
          </div>
        )}

        {data?.skills?.length > 0 && (
          <div className="mb-6">
            <SideLabel>Skills</SideLabel>
            {data.skills.map((sk, i) => (
              <div key={i} className="mb-2">
                <div className="text-[10px] font-bold text-[#38BDF8] uppercase tracking-wider mb-0.5">
                  {rT(sk.name, `skills.${i}.name`, editable, onFill)}
                </div>
                {(sk.items || []).map((item, j) => (
                  <div key={j} className="text-[10px] text-slate-300">
                    · {rT(item, `skills.${i}.items.${j}`, editable, onFill)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {data?.education?.length > 0 && (
          <div className="mb-6">
            <SideLabel>Education</SideLabel>
            {data.education.map((edu, i) => (
              <div key={i} className="mb-3">
                <div className="text-[11px] font-bold">
                  {rT(edu.degree, `education.${i}.degree`, editable, onFill)}
                </div>
                <div className="text-[10px] text-slate-300">
                  {rT(edu.institution, `education.${i}.institution`, editable, onFill)}
                </div>
                <div className="text-[10px] text-[#38BDF8]">
                  {rT(edu.graduation_date, `education.${i}.graduation_date`, editable, onFill)}
                </div>
              </div>
            ))}
          </div>
        )}

        {data?.certifications?.length > 0 && (
          <div>
            <SideLabel>Certifications</SideLabel>
            {data.certifications.map((cert, i) => (
              <div key={i} className="mb-2">
                <div className="text-[11px] font-semibold">
                  {rT(cert.name, `certifications.${i}.name`, editable, onFill)}
                </div>
                {cert.issuer && (
                  <div className="text-[10px] text-slate-400">
                    {rT(cert.issuer, `certifications.${i}.issuer`, editable, onFill)}
                  </div>
                )}
                {cert.date && (
                  <div className="text-[10px] text-[#38BDF8]">
                    {rT(cert.date, `certifications.${i}.date`, editable, onFill)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 px-7 py-7">
        {data?.summary && (
          <Section title="Profile">
            <p className="text-[#444]">{rT(data.summary, "summary", editable, onFill)}</p>
          </Section>
        )}

        {data?.experience?.length > 0 && (
          <Section title="Experience">
            {data.experience.map((exp, i) => (
              <div key={i} className="mb-5">
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-bold text-[14px] text-[#0F172A]">
                    {rT(exp.title, `experience.${i}.title`, editable, onFill)}
                  </span>
                  <span className="text-[10px] text-[#38BDF8] whitespace-nowrap font-mono">
                    {rT(exp.start_date, `experience.${i}.start_date`, editable, onFill)}
                    {" – "}
                    {rT(exp.end_date, `experience.${i}.end_date`, editable, onFill)}
                  </span>
                </div>
                <div className="text-[11px] font-semibold text-[#555] mb-1.5">
                  {rT(exp.company, `experience.${i}.company`, editable, onFill)}
                  {exp.location && (
                    <>{" · "}{rT(exp.location, `experience.${i}.location`, editable, onFill)}</>
                  )}
                </div>
                <ul className="space-y-0.5">
                  {(exp.bullets || []).map((b, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="text-[#38BDF8] font-bold mt-0.5 flex-shrink-0">▸</span>
                      <span className="text-[#333]">
                        {rT(b, `experience.${i}.bullets.${j}`, editable, onFill)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function SideLabel({ children }) {
  return (
    <div className="text-[9px] font-bold uppercase tracking-widest text-[#38BDF8] mb-1.5 pb-0.5 border-b border-slate-700">
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1 h-5 bg-[#38BDF8] rounded-full" />
        <div className="font-bold uppercase text-[11px] tracking-widest text-[#0F172A]">{title}</div>
      </div>
      {children}
    </div>
  );
}
